/**
 * One money cell of the plan or target table: the approved value, a pending
 * chip, and (role permitting) an editor that proposes, saves or overrides.
 * A failed save keeps the input and says why; stale/already-decided offers a
 * reload because only fresh data can fix it.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parseMoneyInput, planInputSchema, targetInputSchema } from '@/services/targets/targetSchemas';
import { announceTargetsChanged } from '@/services/targets/targetsEvents';
import { failureOf, money, reasonRequiredText, type WriteFailure } from './targetUi';

export interface CellSaveInput { value: number; effectiveFrom: string | null; reason: string | null }

export interface TargetCellProps {
  testId: string;
  /** e.g. "2026-10 · Facebook Ads": names the cell's controls for a screen reader. */
  keyLabel: string;
  kind: 'plan' | 'target';
  value: number | null;
  derived?: boolean;
  currency: string;
  pending: { id: string; value: number; mine: boolean } | null;
  /** 'override' = a closed month, master only: needs a reason. */
  edit: 'none' | 'edit' | 'override';
  /** Own proposals can be withdrawn by anyone but a viewer (spec §3: a viewer has no controls). */
  canWithdraw: boolean;
  /** Analyst proposals go to an admin; admin/master edits apply at once. */
  sendsForApproval: boolean;
  /** Target editor only: today, and whether a past date (master, with reason) is allowed. */
  dates?: { today: string; allowPast: boolean };
  onSave: (input: CellSaveInput) => Promise<void>;
  onWithdraw: (id: string) => Promise<void>;
  reload: () => void;
  children?: React.ReactNode;
}

const INPUT = 'w-28 rounded border border-neutral-300 px-2 py-1 text-sm';
const BTN = 'rounded px-2 py-0.5 text-xs font-medium border';

export const TargetCell: React.FC<TargetCellProps> = (p) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [raw, setRaw] = useState('');
  const [date, setDate] = useState(p.dates?.today ?? '');
  const [reason, setReason] = useState('');
  const [failure, setFailure] = useState<WriteFailure | null>(null);
  const [busy, setBusy] = useState(false);

  const isPastDate = p.dates !== undefined && date < p.dates.today;
  // Only the master may backdate a target (with a reason); anyone else is refused before any call.
  const pastRefused = isPastDate && !(p.dates?.allowPast ?? false);
  const needsReason = p.edit === 'override' || (isPastDate && !pastRefused);

  const fail = (message: string) => setFailure({ message, offerReload: false });

  const validate = (n: number): string | null => {
    const check = p.kind === 'plan'
      ? planInputSchema(t).safeParse({ amount: n, currency: p.currency })
      : targetInputSchema(t).safeParse({ target: n, currency: p.currency, effectiveFrom: date });
    return check.success ? null : (check.error.issues[0]?.message ?? t('targets.errors.invalidInput'));
  };

  const submit = async () => {
    const n = parseMoneyInput(raw);
    if (n === null) return fail(t('targets.validation.number'));
    const invalid = validate(n);
    if (invalid) return fail(invalid);
    if (pastRefused) return fail(t('targets.errors.effectivePassed'));
    if (needsReason && !reason.trim()) return fail(reasonRequiredText(t));
    setBusy(true);
    setFailure(null);
    try {
      await p.onSave({ value: n, effectiveFrom: p.dates ? date : null, reason: needsReason ? reason.trim() : null });
      setOpen(false);
      setRaw('');
      setReason('');
      setDate(p.dates?.today ?? '');
      announceTargetsChanged();
      p.reload();
    } catch (err: unknown) {
      setFailure(failureOf(t, err));
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async (id: string) => {
    setBusy(true);
    setFailure(null);
    try {
      await p.onWithdraw(id);
      announceTargetsChanged();
      p.reload();
    } catch (err: unknown) {
      setFailure(failureOf(t, err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <td data-testid={p.testId} className="px-3 py-2 align-top text-sm">
      <div className={p.derived ? 'italic text-neutral-700' : 'text-ink-900'}>
        {p.value === null ? t('targets.none') : money(p.value, p.currency)}
        {p.derived && <span className="ml-1 text-xs">{t('targets.plan.derived')}</span>}
      </div>
      {p.children}
      {p.pending && (
        <div className="mt-1 flex flex-wrap items-center gap-1">
          <span className="rounded-full bg-primary-100 px-2 py-0.5 text-xs text-ink-900">
            {t('targets.pending', { value: money(p.pending.value, p.currency) })}
          </span>
          {p.pending.mine && p.canWithdraw && (
            <button type="button" disabled={busy} className={BTN} onClick={() => p.pending && void withdraw(p.pending.id)}>
              {t('targets.withdraw')}
            </button>
          )}
        </div>
      )}
      {!open && p.edit !== 'none' && (
        <button type="button" className={`${BTN} mt-1`} onClick={() => setOpen(true)}
          aria-label={p.edit === 'override' ? t('targets.overrideClosedAria', { key: p.keyLabel }) : t('targets.editAria', { key: p.keyLabel })}>
          {p.edit === 'override' ? t('targets.overrideClosed') : t('targets.edit')}
        </button>
      )}
      {open && (
        <div className="mt-2 flex flex-col gap-1">
          <input type="text" inputMode="decimal" aria-label={t('targets.amount')} className={INPUT}
            value={raw} onChange={(e) => setRaw(e.target.value)} />
          {p.dates && (
            <input type="date" aria-label={t('targets.cpr.effectiveFrom')} className={INPUT}
              min={p.dates.allowPast ? undefined : p.dates.today} value={date} onChange={(e) => setDate(e.target.value)} />
          )}
          {needsReason && (
            <input type="text" aria-label={t('targets.reasonAria', { key: p.keyLabel })} placeholder={t('targets.reason')} className={INPUT}
              value={reason} onChange={(e) => setReason(e.target.value)} />
          )}
          <div className="flex gap-1">
            <button type="button" disabled={busy} className={`${BTN} bg-ink-950 text-white`} onClick={() => void submit()}>
              {p.sendsForApproval && p.edit === 'edit' ? t('targets.sendForApproval') : t('targets.save')}
            </button>
            <button type="button" className={BTN} onClick={() => { setOpen(false); setFailure(null); }}>
              {t('targets.cancel')}
            </button>
          </div>
        </div>
      )}
      {failure && (
        <div className="mt-1">
          <p role="alert" className="text-xs text-error-700">{failure.message}</p>
          {failure.offerReload && (
            <button type="button" className={BTN} onClick={p.reload}>{t('targets.reload')}</button>
          )}
        </div>
      )}
    </td>
  );
};
